import {
  assign,
  chain,
  cloneDeep,
  find,
  forEach,
  get,
  includes,
  isArray,
  isEmpty,
  isObject,
  keys, merge
} from "lodash-es";
import { getWidgetPropsMap } from './widgets/framework/widget-props';
import { runtimeEventManager } from '@wm/core';
import { 
  propertiesList, getWidgetEventOptions as _getWidgetEventOptionsFromBase, setWidgetEvenOptions as _setWidgetEvenOptionsFromBase,
  setIsMobileApplicationType,
  getIsMobileApplicationType,
  setIsNativeMobileApplicationType,
  getIsNativeMobileApplicationType,
  getLocale,
  setLocale,
  setRoles as _setRoles,
  getRoles,
  setIsTabletApplicationType,
  getIsTabletApplicationType,
  setShowInDeviceOptions,
  getResultProperties,
  getPropertyGroups,
  getAdvancedPropertyGroups
} from './Base';
import { EVENTS_OPTIONS } from "./widgets/framework/constants";

export class PropertiesFactory {

  private unSupportedProperties: any = [];
  private result = {
    properties: <any>{},
    propertyGroups: <any>[],
    advancedPropertyGroups: <any>[]
  };
  private isPrism:boolean = false

  getWidgetEventOptions(): Array<any> {
    return _getWidgetEventOptionsFromBase();
  }

  setWidgetEventOptions(options: Array<any>) {
    _setWidgetEvenOptionsFromBase(options);
  }

  setRoles(pRoles?:any) {
    _setRoles([]);
    if(isArray(pRoles)){
      _setRoles(pRoles);
    }
  }

  private getLocaleFromI18nService(): { [key: string]: string } {

    if(isEmpty(getLocale())) {
      let i18n = runtimeEventManager.getService('i18Services');
      if (i18n) {
        return i18n.getMessages();
      }
    }
    return getLocale();
  }

  updateProps() {
    let projectService = runtimeEventManager.getService('projectService');
    let isWebApplicationType: boolean = true;
    if (projectService) {
      setIsMobileApplicationType(projectService.isMobileApplicationType());
      setIsNativeMobileApplicationType(projectService.isNativeMobileApplicationType());
      setIsMobileApplicationType(projectService.isMobileApplicationType());
      setIsTabletApplicationType(projectService.isTabletApplicationType());
      isWebApplicationType = projectService.isWebApplicationType();
      this.isPrism = projectService.isPrism()
    }

    setLocale(this.getLocaleFromI18nService());
    setShowInDeviceOptions();

    if (getIsMobileApplicationType() || getIsNativeMobileApplicationType()) {
      this.unSupportedProperties = ['shortcutkey', 'tabindex'];
      if (!getIsNativeMobileApplicationType()) {
        this.unSupportedProperties.push('onLongtap');
      }
    } else if (isWebApplicationType) {
      this.unSupportedProperties = ['onLongtap'];
    }
    
    if (!projectService.isPrism()) {
      this.unSupportedProperties ??= [];
      this.unSupportedProperties.push("variant");
    }

    this.result.properties = getResultProperties();
    this.result.propertyGroups = getPropertyGroups();
    this.result.advancedPropertyGroups = getAdvancedPropertyGroups();
  }

  addToPropertiesListParent(widgetType: string, parent: string) {
    if (propertiesList[widgetType] && propertiesList[widgetType].parent) {
      if (!propertiesList[widgetType].parent.includes(parent)) {
        propertiesList[widgetType].parent.push(parent);
      }
    }
  }

  constructor() {
    _setWidgetEvenOptionsFromBase(cloneDeep(EVENTS_OPTIONS));
    runtimeEventManager.on('on-studio-services-initialized').subscribe(() => {
      this.updateProps();
      let widgetPropsMap = getWidgetPropsMap();
    
      if (getIsTabletApplicationType()) {
        let tabWidgets = ['wm-gridcolumn', 'wm-left-panel'];
        tabWidgets.forEach((widgetType: string) => {
          let wp = this.getWidgetProperties(widgetType);
          widgetPropsMap.set(widgetType, wp);
        });
      } else {
        widgetPropsMap.forEach((widgetProps, widgetType) => {
          let wp = this.getWidgetProperties(widgetType) || {};
          widgetPropsMap.set(widgetType, wp);
        });
      }
    });

      
  }

  getWidgetProperties(widgetType: string) {
    let propsObj = get(propertiesList, widgetType);
    if (isEmpty(this.result.properties)) {
      return {};
    }
    const widgetProps = this.getPropertiesOf(propsObj?.widget, propsObj?.parent);
    return widgetProps;
  }

  /*
   If parents array is provided, inject the properties of from the parents into widget and return,
   else return only the properties of the widget.
   */
  getPropertiesOf(widget: any, parents?: any) {
    let widgetProps: any, parentsArr,
      mobileProps,
      advancedProperties: any;
    let projectService = runtimeEventManager.getService('projectService');

    if (widget === 'wm.chart') {
      advancedProperties = this.getAdvancedProperties();
    }

    if (!parents) {
      /* This widget doesn't inherit from other widgets. Fetch the properties of only this widget */
      widgetProps = this.result.properties[widget];
    } else {
      parentsArr = Array.isArray(parents) ? parents : [parents];
      parentsArr.push(widget);
      widgetProps = {};

      /* construct the properties object by inheriting from parents*/
      parentsArr.forEach((parent) => {
        if (!isObject(this.result.properties[parent])) {
          return;
        }
        Object.keys(this.result.properties[parent])
          .forEach((propName) => {
            let propObj = this.result.properties[parent][propName];
            if (!widgetProps[propName]) {
              widgetProps[propName] = {};
            }
            Object.keys(propObj).forEach((key) => {
                widgetProps[propName][key] = propObj[key];
            });
          });
      });
    }
    if (widget && (getIsTabletApplicationType() || getIsMobileApplicationType())) {
      if (getIsNativeMobileApplicationType()) {
        assign(widgetProps, this.result.properties[widget + '.tab'], this.result.properties[widget + '.native_mobile']);
      } else {
        assign(widgetProps, this.result.properties[widget + '.mobile'], this.result.properties[widget + '.tab']);
      }
    }
    if (getIsNativeMobileApplicationType()) {
      // assigning native mobile specific properties through widget names which are suffixed by '.native_mobile'
      forEach(parents, (p) => {
        assign(widgetProps, this.result.properties[p + '.native_mobile']);
      });
    }
    if (getIsTabletApplicationType()) {
      // assigning mobile specific properties through widget names which are suffixed by '.mobile'
      forEach(parents, (p) => {
        assign(widgetProps, this.result.properties[p + '.tab']);
      });
    }

     if (this.isPrism) {
      // assigning prism specific properties through widget names which are suffixed by '.prism_only'
      forEach(parents, (p) => {
        assign(widgetProps, this.result.properties[p + '.prism']);
      });

      if(projectService.isWebApplicationType()) {
        forEach(parents, (p) => {
          assign(widgetProps, this.result.properties[p + '.web_prism']);
        });
      }
      if(getIsNativeMobileApplicationType()) {
        forEach(parents, (p) => {
          assign(widgetProps, this.result.properties[p + '.native_mobile_prism']);
        });
      }
    }else{
       // assigning non prism specific properties through widget names which are suffixed by '.non_prism_only'
      forEach(parents, (p) => {
        assign(widgetProps, this.result.properties[p + '.non_prism']);
      });
    }
   
    if (widgetProps) {
      forEach(this.unSupportedProperties, (key) => {
        if (widgetProps[key]) {
          delete widgetProps[key];
        }
      });
      chain(keys(widgetProps).filter((key) => {
        return !!widgetProps[key].ignore;
      }).forEach((key) => {
        delete widgetProps[key];
      })).value;
    }

    /* Inject show and disabled fields into each property object */
    // if (CONSTANTS.isStudioMode) { // commenting condition because isStudioMode is true - CHECK LATER
    keys(widgetProps)
      .forEach((key) => {
        let property = widgetProps[key];
        if (!property.hasOwnProperty('show')) {
          property.show = true;
        }
        if (advancedProperties && includes(advancedProperties, key) && !property.hasOwnProperty('showindesigner')) {
          property.showindesigner = true;
        }
        property.disabled = property.disabled || false;
        // Hide properties with hideInPrism: true if project is Prism
        if (projectService.isPrism()) {
          if (property.hideInPrism) {
            property.show = false
          }
        }
      });
    // } else {
    //   Object.keys(widgetProps).forEach(function (propName) {
    //     var propDetails = widgetProps[propName];
    //     widgetProps[propName] = pick(propDetails, ['type', 'value', 'bindable', 'displaytype']);
    //   });
    // }

    return widgetProps;
  }
  //gives all the advanced properties list
  getAdvancedProperties() {
    let advancedProperties: any = [];
    forEach(this.result.advancedPropertyGroups, (group: { parent: any, properties: any }) => {
      if (group.parent) {
        forEach(group.properties, function (prop) {
          advancedProperties.push(prop);
        });
      }
    });
    return advancedProperties;
  }
  getPropertyGroups() {
    return this.result.propertyGroups;  
  }
  getAdvancedPropertyGroups() {
    return this.result.advancedPropertyGroups;
  }
  getPrimaryPropertyGroups(group?: any) {
    let groupProperties: any = [];
    this.result.propertyGroups.forEach((propertyGroup: any) => {
      if (propertyGroup.parent === group) {
        groupProperties = groupProperties.concat(propertyGroup.properties);
      }
    });
    return groupProperties;

  }
  getGroupProperties(group?: any) {
    let groupProperties: any = [];
    this.result.propertyGroups.forEach((propertyGroup: any) => {
      if (propertyGroup.parent === group) {
        groupProperties = groupProperties.concat(propertyGroup.properties);
      }
    })

  }
  getPropertyGroup(name?:any) {
    return find(this.result.propertyGroups, (group) => {
      return group.name === name;
    });
  }
}

export let propFactory = new PropertiesFactory();
